import React, { useEffect, useRef } from 'react';
import { HilChatMessageItem } from './HilChatMessageItem';
import type { HilChatMessage } from '../types/HilChatMessage';
import type { HilChatWindowSettings } from '../settings/HilChatWindowSettings';

interface HilChatTranscriptProps {
  messages: HilChatMessage[];
  settings: HilChatWindowSettings;
  /** Messages whose order is strictly before currentStep are already-submitted and rendered read-only. */
  currentStep: number;
  onFormValuesChange?: (messageID: string, values: Record<string, unknown>) => void;
  onRetry?: (messageID: string) => void;
}

/** Owns scroll position only — the merged message list itself is owned by HilChatWindow. */
export function HilChatTranscript({ messages, settings, currentStep, onFormValuesChange, onRetry }: HilChatTranscriptProps): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  if (messages.length === 0) {
    return (
      <div className="hil-chat-transcript hil-chat-transcript--empty" ref={scrollRef}>
        <p className="hil-chat-transcript__empty-state">No messages yet.</p>
      </div>
    );
  }

  return (
    <div className="hil-chat-transcript" ref={scrollRef} aria-live="polite">
      {messages.map(message => (
        <HilChatMessageItem
          key={message.id}
          message={message}
          settings={settings}
          disabled={message.order < currentStep}
          onFormValuesChange={values => onFormValuesChange?.(message.id, values)}
          onRetry={() => onRetry?.(message.id)}
        />
      ))}
    </div>
  );
}
